/**
 * SCENE sân đấu — chỉ lo HIỂN THỊ + nhịp thời gian.
 * Toàn bộ luật chơi nằm trong sim/ (thuần, tất định).
 */
import Phaser from 'phaser';
import { World, ARENA, type LineupEntry, type SimEvent } from '../sim/world';
import { ArenaView } from '../render/arena';
import { UnitViews } from '../render/unitview';
import { FxLayer } from '../render/fx';
import { ProjectileLayer } from '../render/projectiles';
import { DT } from '../sim/formulas';
import { t } from '../i18n';

export interface SceneHooks {
  onWorldEvent?: (e: SimEvent) => void;
  onStatusChange?: (status: World['status']) => void;
  /** người chơi click/ chạm vào sân để ra lệnh di chuyển cho tướng đang chọn */
  onCommandMove?: (x: number, y: number) => void;
  /**
   * Bấm/ chạm TRÚNG một tướng trên sân.
   * Yêu cầu người dùng: "ở màn hình ingame play, tôi click vào hero nào thì sẽ là chọn hero
   * đó thay vì bắt buộc phải chọn ở mục bên dưới".
   * Trả về `true` nếu đã "tiêu thụ" cú bấm (để KHÔNG ra lệnh di chuyển nữa).
   */
  onPickHero?: (unitId: number) => boolean;
}

export class ArenaScene extends Phaser.Scene {
  world: World | null = null;
  arena!: ArenaView;
  units!: UnitViews;
  fx!: FxLayer;
  /** lớp vẽ ĐƯỜNG ĐẠN (xem `render/projectiles.ts`) */
  shots!: ProjectileLayer;
  speed = 1;
  paused = false;
  hooks: SceneHooks = {};

  private acc = 0;
  private frameCount = 0;
  private lastStatus: World['status'] = 'prep';
  private fpsText?: Phaser.GameObjects.Text;

  constructor() {
    super('arena');
  }

  create(): void {
    this.arena = new ArenaView(this);
    this.units = new UnitViews(this);
    this.fx = new FxLayer(this);
    // ĐƯỜNG ĐẠN: trước đây `sim` có đạn mà render không vẽ ⇒ đòn xa vô hình (lỗi người dùng báo)
    this.shots = new ProjectileLayer(this);
    this.fpsText = this.add
      .text(8, 6, '', { fontFamily: 'ui-monospace, monospace', fontSize: '11px', color: '#a8a29e' })
      .setDepth(9000)
      .setAlpha(0.75);
    /*
     * ⚠️ CAMERA PHẢI GIỮ ĐÚNG CỠ THIẾT KẾ (ARENA.w × ARENA.h), KHÔNG theo `scale.width`.
     *
     * Lỗi đã dính (đo được): sau khi vào trận từ trang chủ, `scale.width/height` = 1576×404
     * (bằng khung chứa) nên camera bị đặt thành 1576×404 trong khi THẾ GIỚI chỉ 1000×600 ⇒
     * `getWorldPoint()` trả toạ độ SAI và mọi cú bấm trên sân lệch hẳn (bấm giữa sân ra
     * orderX 787 thay vì ~500).
     * Cách đúng: camera giữ cỡ thiết kế; Phaser ở chế độ FIT tự co giãn ảnh ra khung chứa,
     * nên `getWorldPoint` luôn quy đổi đúng.
     */
    this.cameras.main.setViewport(0, 0, ARENA.w, ARENA.h);
    /*
     * ⚠️⚠️ LISTENER `resize` PHẢI LÀ HÀM CÓ TÊN + PHẢI GỠ Ở `SHUTDOWN` — BẪY ĐÃ SẬP (bàn giao §3.5).
     *
     * Bản cũ là hàm VÔ DANH:
     *     this.scale.on('resize', () => this.cameras.main.setViewport(0, 0, ARENA.w, ARENA.h));
     * `this.scale` là ScaleManager CẤP GAME (không phải của scene), nên listener này sống NGOÀI vòng
     * đời scene. Hàm vô danh thì KHÔNG có tham chiếu ⇒ KHÔNG BAO GIỜ gỡ được.
     *
     * Hậu quả ĐÃ ĐO ĐƯỢC TRONG CHROME THẬT (6–12 lỗi mỗi lần vào Arena): sau khi ai đó gọi
     * `stop('arena')`, Phaser bắn `SHUTDOWN` và huỷ camera/ display list. Nhưng listener cũ vẫn nằm
     * trong ScaleManager, nên mỗi lần `Hud.showScreen` chạy vòng poll `game.resizeArena()` (phát
     * `resize` + `scale.refresh()`) là nó chạy trên `cameras.main` ĐÃ BỊ HUỶ ⇒
     *     `TypeError: Cannot read properties of undefined (reading 'setViewport')`
     * và vì exception ném TRONG `scale.refresh()` nên nó làm HỎNG luôn nhịp đo lại kích thước của Phaser.
     *
     * ⚠️ VÌ SAO HIỆN CHƯA THẤY LỖI: `game.ts` đang đưa scene này vào `sleep()` (KHÔNG `stop()`) khi mở
     * Arena, nên `SHUTDOWN` không bắn. Đây là bug TIỀM ẨN: mọi đường `stop()`/ `restart()` scene sau
     * này sẽ kích hoạt lại đúng triệu chứng cũ. Sửa ở đây rẻ và không đổi hành vi đường đang chạy
     * (`sleep()` không bắn `SHUTDOWN` ⇒ `dispose()` không chạy).
     *
     * Cách làm giống hệt `ArenaBattleScene` (dòng ~246/621) — repo đã có sẵn khuôn đúng, dùng lại
     * thay vì phát minh: hàm có tên + truyền `this` làm ngữ cảnh để `on`/`off` khớp ĐÚNG một tham chiếu.
     */
    this.scale.on('resize', this.onScaleResize, this);
    // dọn khi scene shutdown (vào/ ra nhiều lần trong một phiên)
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.dispose, this);

    // ĐIỀU KHIỂN DI CHUYỂN: click/ chạm (hoặc kéo) trên sân
    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => this.issueMove(p, false));
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      /*
       * ⚠️ ĐANG KÉO THÌ KHÔNG ĐƯỢC ĐỔI TƯỚNG.
       *
       * LỖI NGƯỜI DÙNG BÁO: "click vào 1 nhân vật rồi giữ chặt rồi miết đi nơi khác, nếu chẳng
       * may miết vào nhân vật khác đột nhiên bị đổi nhân vật".
       * Nguyên nhân: mỗi sự kiện `pointermove` khi đang giữ đều gọi `issueMove()`, mà
       * `issueMove` lại dò tướng dưới con trỏ ⇒ miết ngang qua tướng khác là CHỌN nó.
       * Sửa: chỉ dò/ chọn tướng ở `pointerdown`; khi đang kéo (`dragging = true`) thì bỏ qua
       * phần chọn, chỉ ra lệnh di chuyển cho tướng ĐANG CHỌN.
       */
      if (p.isDown) this.issueMove(p, true);
    });
    this.input.on('pointerup', () => {
      this.dragging = false;
    });
    this.moveMarker = this.add.graphics().setDepth(8000).setVisible(false);
    /*
     * CON THOI TRÊN ĐẦU TƯỚNG ĐANG CHỌN.
     * Yêu cầu người dùng: "khi tôi chọn 1 character, tôi muốn có con thoi trỏ trên đầu nhân vật
     * để nhận biết".
     * Vẽ bằng `Graphics` RIÊNG (không phải sprite) vì: rẻ (1 đối tượng cho cả sân), không tốn
     * texture, và luôn nằm trên mọi mảnh rig nên không bị tay/ tóc che.
     */
    this.selectMarker = this.add.graphics().setDepth(8500);
  }

  /**
   * KHUNG CHỨA ĐỔI CỠ ⇒ ép lại viewport về CỠ THIẾT KẾ (`ARENA.w × ARENA.h`).
   *
   * ⚠️ Ở dạng HÀM CÓ TÊN để `scale.off()` gỡ được đúng tham chiếu này (xem chú thích dài ở `create`).
   * KHÔNG đổi thành hàm vô danh: làm vậy là trả lại đúng bug rò rỉ listener vừa sửa.
   */
  private onScaleResize(): void {
    this.cameras.main.setViewport(0, 0, ARENA.w, ARENA.h);
  }

  /**
   * DỌN KHI SCENE `SHUTDOWN` — gỡ listener cấp GAME mà Phaser không tự dọn.
   *
   * ⚠️ VÌ SAO CHỈ GỠ `scale`: `this.input.on(...)` (pointerdown/ move/ up) nằm trên `InputPlugin` CỦA
   * SCENE — Phaser tự huỷ chúng khi shutdown. Còn `this.scale` là ScaleManager CẤP GAME, sống ngoài
   * vòng đời scene ⇒ chỉ có ta gỡ được.
   *
   * ⚠️ KHÔNG gọi `this.cameras.main` hay chạm GameObject ở đây: lúc `SHUTDOWN` chạy, camera/ display
   * list ĐANG bị huỷ. Việc dọn sprite là của `Phaser` và của `Game.exitArena()`; ở đây chỉ tháo đúng
   * thứ rò rỉ (listener).
   */
  private dispose(): void {
    this.scale.off('resize', this.onScaleResize, this);
  }

  private moveMarker?: Phaser.GameObjects.Graphics;
  /** con thoi chỉ tướng đang chọn (vẽ mỗi khung hình) */
  private selectMarker?: Phaser.GameObjects.Graphics;
  /** id tướng đang được chọn — do Game đẩy sang */
  selectedHeroUnitId: number | null = null;
  /** true khi người chơi đang GIỮ và miết trên sân */
  private dragging = false;
  private lastMarkerAt = 0;

  /**
   * Tìm tướng dưới con trỏ. Trả về id đơn vị, hoặc -1.
   *
   * Ưu tiên hình HỘP BAO (rộng × cao thật của rig); nếu không trúng hộp nào thì lùi về
   * khoảng cách tâm (bán kính + 6px) để vẫn bấm được khi nhân vật đang nghiêng/ co.
   */
  private pickHeroAt(wx: number, wy: number): number {
    const w = this.world;
    if (!w) return -1;
    let best = -1;
    let bestD = Infinity;
    for (const u of w.units) {
      if (!u.alive || u.team !== 'hero') continue;
      const view = this.units.viewOf(u.id);
      // vùng bấm: thân rộng ~1,6× bán kính, cao ~2,2× (nhân vật đứng)
      const halfW = u.radius * 1.6;
      const halfH = u.radius * 2.4;
      const dx = Math.abs(wx - u.x);
      const dy = Math.abs(wy - u.y);
      const inside = dx <= halfW && dy <= halfH;
      const near = Math.hypot(dx, dy) <= u.radius + 6;
      if (!inside && !near) continue;
      /*
       * Nhiều tướng đứng chồng nhau thì chọn con GẦN CON TRỎ NHẤT, và ưu tiên con đang được
       * chọn sẵn (bấm lại vào con đang chọn = không đổi gì, tránh nhảy lung tung).
       */
      const d = Math.hypot(dx, dy) - (view ? 0 : 100);
      if (d < bestD) {
        bestD = d;
        best = u.id;
      }
    }
    return best;
  }

  /**
   * VẼ CON THOI TRÊN ĐẦU TƯỚNG ĐANG CHỌN.
   *
   * Nhấp nhô nhẹ theo thời gian (sin) để mắt bắt được ngay giữa bầy quái đông, và có bóng mờ
   * phía dưới để thấy được cả khi nhân vật đứng trước nền sáng.
   * Chỉ vẽ khi tướng CÒN SỐNG — tướng gục thì bỏ, không chỉ vào xác.
   */
  /**
   * BÁO TRẠNG THÁI ĐANG CHỜ (nếu có) — GỌI ĐƯỢC TỪ NGOÀI.
   *
   * ⚠️⚠️ VÌ SAO CẦN (lỗi thật, đo được): khi ở TRANG CHỦ, vòng lặp Phaser bị cho NGỦ để đỡ
   * giật, nên `update()` KHÔNG chạy ⇒ việc phát hiện đổi trạng thái cũng không chạy. Nếu trận
   * kết thúc (`won`) đúng lúc đang ở trang chủ thì `onStatusChange('won')` bị HOÃN lại, và nó
   * chỉ bắn ở khung hình đầu tiên sau khi thức dậy — tức có thể SAU KHI người chơi đã bắt đầu
   * trận mới. Hệ quả đo được trong `verify-web`: "vào trận làm đổi ví tổng (4317,99 → 5609)" —
   * tiền thưởng của trận CŨ rơi vào ví ở thời điểm chẳng liên quan.
   * Nay: cho phép gọi chủ động để CHỐT trạng thái đang chờ trước khi dựng trận mới/ trước khi
   * cho vòng lặp chạy lại. `lastStatus` được cập nhật ngay nên không bao giờ trả thưởng hai lần.
   */
  flushStatusChange(): void {
    const w = this.world;
    if (!w) return;
    if (w.status !== this.lastStatus) {
      this.lastStatus = w.status;
      this.hooks.onStatusChange?.(w.status);
    }
  }

  private drawSelectMarker(w: World): void {
    const g = this.selectMarker;
    if (!g) return;
    g.clear();
    if (this.selectedHeroUnitId === null) return;
    const u = w.units.find((x) => x.id === this.selectedHeroUnitId && x.team === 'hero' && x.alive);
    if (!u) return;

    const t = this.time.now / 1000;
    const bob = Math.sin(t * 4.2) * 3.5; // nhấp nhô ~0,7 lần/giây

    /*
     * ⚠️ ĐỘ CAO CON THOI PHẢI TÍNH THEO CHIỀU CAO RIG, KHÔNG THEO BÁN KÍNH.
     *
     * LỖI NGƯỜI DÙNG BÁO: "con trỏ trên đầu character đang nằm ở BỤNG nhân vật".
     * Nguyên nhân: đặt ở `u.y - u.radius * 3.1`. Nhân vật cao ~105px nhưng `radius` chỉ ~20
     * (dùng cho va chạm) ⇒ 3,1 × 20 = 62px, KHÔNG tới đầu. Đo được: con thoi nằm ở **66% chiều
     * cao cơ thể** tính từ chân lên (tức ngang bụng) và cách đỉnh đầu **36px**.
     *
     * Cách đúng: lấy hộp bao THẬT của các mảnh rig (sprite trên cùng) rồi đặt con thoi trên
     * đỉnh đó. Nhờ vậy đúng cho mọi cỡ: quái nhỏ, boss to gấp đôi, và cả khi rig đổi tỉ lệ.
     */
    /*
     * ⚠️ PHẢI CAO HƠN CẢ THANH MÁU VÀ TÊN TƯỚNG.
     *
     * Người dùng: "con trỏ trên đầu character phải cao hẳn lên nhé, cao hơn thanh máu 1 chút".
     * Đo hình học thật (xem `src/render/unitview.ts`):
     *   · thanh máu tướng: y = u.y − 104, cao 5px  ⇒ mép trên ở −106,5
     *   · thanh mana:      y = u.y − 98
     *   · tên tướng:       y = u.y − 114          ⇒ mép trên ở −114 − cỡ chữ
     * Vì vậy con trỏ phải nằm TRÊN −114; lấy thêm một khoảng cho tên + nhấp nhô.
     * Tính theo HỘP BAO RIG (đúng cho mọi cỡ: quái nhỏ, boss to gấp đôi) rồi mới so với mốc
     * thanh máu/ tên, lấy giá trị CAO HƠN (nhỏ hơn theo trục y) để không bao giờ đè lên chúng.
     */
    const view = this.units.viewOf(u.id);
    let topY = u.y - u.radius * 3.1; // dự phòng khi chưa có rig
    if (view && view.sprites.length) {
      let minTop = Infinity;
      for (const sp of view.sprites) {
        if (!sp.visible) continue;
        const h = sp.frame.height * sp.scaleY;
        minTop = Math.min(minTop, sp.y - h / 2);
      }
      if (Number.isFinite(minTop)) topY = minTop - u.radius * 0.55; // nhô lên trên đỉnh đầu
    }
    /*
     * TRẦN TRÊN: CAO HƠN CẢ TÊN TƯỚNG MỘT KHOẢNG RÕ RÀNG.
     *
     * Người dùng: "con trỏ nhân vật cho cao hơn tên character 1 chút nhé".
     * Mốc thật: tên tướng ở `u.y − 114`, cỡ chữ ~12px ⇒ MÉP TRÊN tên ở khoảng `u.y − 120`.
     * Bản trước lấy `u.y − 132` ⇒ chỉ cao hơn mép trên tên 11,5px, mà con trỏ còn NHẤP NHÔ ±3,5px
     * nên có lúc gần chạm tên. Nay lấy `u.y − 150` ⇒ cao hơn mép trên tên **~30px**, dư sức cho
     * nhấp nhô và cho tên dài xuống dòng.
     * Quái/ boss: thanh máu của chúng ở `u.y − radius×5,4`; lấy thêm 44px cho khỏi đè.
     */
    const aboveBars = u.team === 'hero' ? u.y - 150 : u.y - (u.radius * 5.4) - 44;
    topY = Math.min(topY, aboveBars);
    topY += bob;
    const wHalf = u.radius * 0.95;

    // tam giác chỉ xuống
    g.fillStyle(0xfbbf24, 0.95);
    g.beginPath();
    g.moveTo(u.x - wHalf, topY);
    g.lineTo(u.x + wHalf, topY);
    g.lineTo(u.x, topY + wHalf * 1.25);
    g.closePath();
    g.fillPath();
    // viền tối cho nổi trên mọi nền
    g.lineStyle(1.5, 0x3b2a12, 0.9);
    g.strokePath();

    // vòng chân tướng
    g.lineStyle(2, 0xfbbf24, 0.85);
    g.strokeEllipse(u.x, u.y + u.radius * 0.35, u.radius * 2.3, u.radius * 0.9);
    g.lineStyle(1, 0xfff3c4, 0.5);
    g.strokeEllipse(u.x, u.y + u.radius * 0.35, u.radius * 1.7, u.radius * 0.65);
  }

  private issueMove(p: Phaser.Input.Pointer, isDrag: boolean): void {
    const wp = this.cameras.main.getWorldPoint(p.x, p.y);

    /*
     * ƯU TIÊN CHỌN TƯỚNG: bấm trúng một tướng thì CHỌN nó, không ra lệnh di chuyển.
     *
     * Vì sao phải dò "trúng ai" theo HÌNH HỘP BAO chứ không theo tâm đơn vị: nhân vật cao
     * ~104px nên bấm vào đầu/ chân mà chỉ so tâm (bán kính ~20px) thì trượt — người chơi sẽ
     * tưởng "bấm không ăn". Hộp bao rộng bằng thân và cao bằng cả người là vùng bấm tự nhiên.
     *
     * ⚠️ CHỈ DÒ/ CHỌN Ở CÚ BẤM ĐẦU (`isDrag === false`). Khi đang giữ và miết mà vẫn dò thì
     * miết ngang qua tướng khác là ĐỔI TƯỚNG GIỮA CHỪNG — đúng lỗi người dùng báo.
     */
    if (!isDrag) {
      this.dragging = true;
      const picked = this.pickHeroAt(wp.x, wp.y);
      if (picked >= 0 && this.hooks.onPickHero?.(picked)) return;
    }
    this.hooks.onCommandMove?.(wp.x, wp.y);
    const now = this.time.now;
    if (this.moveMarker && now - this.lastMarkerAt > 60) {
      this.lastMarkerAt = now;
      const g = this.moveMarker;
      g.clear();
      g.lineStyle(3, 0xf5b942, 0.95);
      g.strokeCircle(wp.x, wp.y, 18);
      g.lineStyle(2, 0xf5b942, 0.5);
      g.strokeCircle(wp.x, wp.y, 26);
      g.setAlpha(1).setScale(1).setVisible(true);
      this.tweens.add({
        targets: g,
        alpha: 0,
        scaleX: 1.4,
        scaleY: 1.4,
        duration: 420,
        onComplete: () => g.setVisible(false),
      });
    }
  }

  attachWorld(world: World, lineup: LineupEntry[]): void {
    this.fx.clear();
    this.shots.clear();
    this.units.reset();
    this.world = world;
    this.units.setLineup(lineup);
    this.acc = 0;
    this.lastStatus = world.status;
  }

  /** Gọi từ UI: dùng bình / niệm chiêu */
  usePotion(kind: 'hp' | 'mana' | 'stamina', heroId: number): { ok: boolean; message: string } {
    if (!this.world) return { ok: false, message: t('Chưa vào trận để chơi', 'Not in battle yet') };
    return this.world.usePotion(kind, heroId);
  }

  buyAmmo(heroId: number): { ok: boolean; message: string } {
    if (!this.world) return { ok: false, message: t('Chưa vào trận để chơi', 'Not in battle yet') };
    return this.world.buyAmmo(heroId);
  }

  castSkill(heroId: number, index: number): boolean {
    if (!this.world) return false;
    const hero = this.world.units.find((u) => u.id === heroId && u.team === 'hero');
    if (!hero) return false;
    return this.world.castSkill(hero, index);
  }

  /**
   * ĐỔI NGÔN NGỮ: cập nhật những chữ vẽ bằng PHASER (không phải DOM) — nhãn tên tướng/ quái và
   * nhãn dưới Nhà. Phần DOM do `Hud.render()` lo (xem `Game.setLanguage`).
   */
  refreshLang(): void {
    this.units.refreshNameTags();
    this.arena.refreshLang();
  }

  update(_time: number, deltaMs: number): void {
    const w = this.world;
    if (!w) return;

    const realDt = Math.min(0.1, deltaMs / 1000);

    if (!this.paused) {
      /*
       * ĐỨNG HÌNH VA CHẠM (hit-stop): đòn nặng thì mô phỏng khựng lại vài chục ms.
       * Đây là mẹo kinh điển để cú đánh "có sức nặng" — mắt người đọc được va chạm.
       * Mô phỏng chỉ ĐẶT cờ, việc khựng nằm ở lớp hiển thị nên sim vẫn tất định.
       */
      if (w.hitStop > 0) {
        w.hitStop = Math.max(0, w.hitStop - realDt);
      } else {
        // BƯỚC CỐ ĐỊNH 30Hz — mô phỏng không phụ thuộc FPS
        this.acc += realDt * this.speed;
        let steps = 0;
        while (this.acc >= DT && steps < 12) {
          w.update(DT);
          this.acc -= DT;
          steps++;
        }
        if (steps >= 12) this.acc = 0; // tránh spiral of death
      }
    }

    // VỤ NỔ LỚN ⇒ rung màn hình + đẩy camera (do sim báo qua shockPower)
    if (w.shockPower > 0.25) {
      this.fx.shake(w.shockPower);
      if (w.shockPower > 0.75) this.fx.zoomPunch(w.shockPower);
      w.shockPower = 0;
    }

    // đẩy sự kiện ra UI
    if (w.events.length) {
      for (const e of w.events) {
        // chiêu "nặng" thì rung màn hình + gọi tên chiêu giữa màn hình cho "hoành tráng"
        if (e.type === 'cast' && (e.power ?? 0) >= 3) this.fx.shake(e.power ?? 3);
        if (e.type === 'cast' && (e.power ?? 0) >= 4 && e.text) this.fx.callout(e.text, 0xfde68a, e.power ?? 4);
        // Nhà bị đập ⇒ nháy đỏ cả màn hình, người chơi biết mình đang thua
        if (e.type === 'coreHit') this.fx.dangerFlash(0.32);
        this.hooks.onWorldEvent?.(e);
      }
      w.events.length = 0;
    }
    this.flushStatusChange();

    this.arena.update(w.core);
    this.units.sync(realDt, w);
    this.shots.render(w.projectiles);
    this.drawSelectMarker(w);
    /* truyền `w` để lớp hiệu ứng vẽ vùng cháy + lửa trên người quái đang cháy (đọc thẳng từ sim) */
    this.fx.render(w.floats, w.bursts, realDt, w);

    if (this.fpsText) {
      this.fpsText.setText(
        t(
          '{fps} FPS · đơn vị {u} · sprite {s} · vải {v}',
          '{fps} FPS · units {u} · sprites {s} · sheets {v}',
          { fps: Math.round(this.game.loop.actualFps), u: w.units.length, s: this.units.spriteCount, v: this.units.factory.textureCount },
        ),
      );
    }
    // số liệu cho kiểm thử tự động (Playwright đọc window.__TDK_PERF__)
    this.frameCount++;
    if (this.frameCount % 15 === 0) {
      (globalThis as { __TDK_PERF__?: Record<string, number> }).__TDK_PERF__ = this.debugInfo();
    }
  }

  /** Cho công cụ đo hiệu năng / chụp ảnh tự động. */
  debugInfo(): Record<string, number> {
    return {
      fps: Math.round(this.game.loop.actualFps),
      units: this.world?.units.length ?? 0,
      sprites: this.units.spriteCount,
      partTextures: this.units.factory.textureCount,
      arenaW: ARENA.w,
      arenaH: ARENA.h,
      aiParts: this.units.aiLoaded,
      vfxCells: this.fx.aiCellCount,
    };
  }
}
